import Decimal from 'decimal.js';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { InfoIcon } from '@/lib/components/InfoIcon';
import { NoOffersHint } from '@/lib/components/OrderForm/OrderSummary/NoOffersHint';
import { AnimatedUSDText } from '@/lib/components/Text/AnimatedUSDText';
import { useOrderBookDataById } from '@/lib/hooks/useOrderBookServiceById';
import { useFees } from '@/lib/providers/FeesProvider';
import { useOrderFormStore } from '@/lib/store/order-form-store';
import { Text } from '@/lib/ui-kit/text';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/lib/ui-kit/tooltip';
import { calculateMarketSellProceeds } from '@/lib/utils/calculateAvgMarketPrice';
import { proceedsAfterFee } from '@/lib/utils/calculateFees';

export const MarketSellSummary = () => {
  const t = useTranslations('orderForm');
  const outcomeId = useOrderFormStore.use.selectedOutcomeId();
  const intent = useOrderFormStore.use.intent();
  const sharesQuantity = useOrderFormStore.use.sharesCount();
  const orderBook = useOrderBookDataById(outcomeId);

  // Check if there are bids in the order book
  const hasBids = useMemo(() => {
    if (!orderBook) return false;
    const bids = orderBook[intent].bids;
    return bids && bids.length > 0;
  }, [orderBook, intent]);

  const { fees } = useFees();

  // Net of the sell taker fee — the figure that lands on the balance. Only the
  // shares the book absorbs bring money in.
  const receivingAmount = useMemo(() => {
    const gross = orderBook
      ? calculateMarketSellProceeds(orderBook[intent].bids, sharesQuantity)
      : Decimal(0);
    return fees ? proceedsAfterFee(gross, fees) : gross;
  }, [orderBook, intent, sharesQuantity, fees]);

  if (!fees) {
    return null; // Don't render if fees are not loaded yet
  }

  return (
    <Text variant="body-small" className="flex w-full items-center justify-between">
      <span className="flex items-center gap-1 text-v2-text-primary opacity-50">
        <Text variant="body-small">{t('receivingAmount')}</Text>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <InfoIcon />
            </span>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            sideOffset={8}
            className="!w-fit max-w-[280px] px-2 py-1.5 !text-left"
          >
            <Text variant="label-md">{t('receivingAmountTooltip')}</Text>
          </TooltipContent>
        </Tooltip>
      </span>
      <span className={hasBids ? 'text-v2-green' : 'text-v2-text-primary'}>
        {hasBids ? (
          <AnimatedUSDText value={receivingAmount} alwaysShowDecimals />
        ) : (
          <NoOffersHint />
        )}
      </span>
    </Text>
  );
};
